import { getJwtExp, type OktaTokenBearer, resolveOktaAccessToken } from "@/util/auth";

/** Root state fields this composable reads (structural typing — full Vuex state may include more). */
export type AccessTokenRefreshRootState = {
  currentUser: OktaTokenBearer | null;
};

export type AccessTokenRefreshStore = {
  readonly state: AccessTokenRefreshRootState;
  dispatch(action: "refreshUserDataFromServer"): Promise<void>;
  watch<P>(
    getter: (state: AccessTokenRefreshRootState) => P,
    callback: (newValue: P, oldValue: P) => void,
    options?: { immediate?: boolean },
  ): () => void;
};

const isNonEmptyToken = (value: unknown): value is string => typeof value === "string" && value.trim() !== "";

const STALE_TOKEN_RETRY_MS = 30 * 1000;

export const useAccessTokenRefresh = (
  store: AccessTokenRefreshStore,
): { scheduleOktaAccessTokenRefresh: () => void } => {
  const scheduleOktaAccessTokenRefresh = (): void => {
    // Mirrors EnsureOktaTokenIsValid-style behavior: `refreshUserDataFromServer` loads `/user` (session
    // cookie), and the server returns an updated user payload including a fresh Okta access token.
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const clear = (): void => {
      if (refreshTimer !== undefined) {
        clearTimeout(refreshTimer);
        refreshTimer = undefined;
      }
    };

    const scheduleFromToken = (token: string): void => {
      clear();

      const exp = getJwtExp(token);
      if (exp === undefined) return;

      const expMs = exp * 1000;
      const nowMs = Date.now();
      const refreshMs = 20 * 1000; // 20 seconds before exp (last-chance vs request interceptor window)
      const targetMs = expMs - refreshMs - nowMs;
      const delayMs = targetMs > 0 ? targetMs : STALE_TOKEN_RETRY_MS;

      refreshTimer = setTimeout(() => {
        void (async (): Promise<void> => {
          // Avoid background refreshes while the tab is hidden; visibilitychange reschedules.
          if (document.visibilityState !== "visible") return;

          try {
            await store.dispatch("refreshUserDataFromServer");
          } catch {
            // `refreshUserDataFromServer` handles logout on failure.
          } finally {
            const nextToken = resolveOktaAccessToken(store.state.currentUser);
            if (isNonEmptyToken(nextToken)) {
              scheduleFromToken(nextToken);
            }
          }
        })();
      }, delayMs);
    };

    // Watch token changes (including persisted state hydration and post-refresh updates).
    store.watch(
      (state) => resolveOktaAccessToken(state.currentUser),
      (token) => {
        if (isNonEmptyToken(token)) {
          scheduleFromToken(token);
        } else {
          clear();
        }
      },
      { immediate: true },
    );

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        const token = resolveOktaAccessToken(store.state.currentUser);
        if (isNonEmptyToken(token)) {
          scheduleFromToken(token);
        }
      }
    });
  };

  return {
    scheduleOktaAccessTokenRefresh,
  };
};
